import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Clock, AlertCircle, CheckCircle2, ChevronDown } from 'lucide-react';
import { formatGuestTime, parseLocalDate } from './guestDates';

// Inject custom animations
const style = document.createElement('style');
style.textContent = `
  @keyframes glow-pulse {
    0%, 100% {
      opacity: 0.3;
      transform: scale(1);
    }
    50% {
      opacity: 0.8;
      transform: scale(1.15);
    }
  }

  @keyframes icon-bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-8px); }
  }

  @keyframes scale-pulse {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.1); }
  }

  @keyframes slide-down {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
`;
if (!document.head.querySelector('style[data-checkout-reminder-animations]')) {
  style.setAttribute('data-checkout-reminder-animations', 'true');
  document.head.appendChild(style);
}

interface CheckoutReminderProps {
  checkOutDate: string;
  checkOutTime: string | null;
  checkOutInstructions: string | null;
  darkMode: boolean;
}

export function CheckoutReminder({
  checkOutDate,
  checkOutTime,
  checkOutInstructions,
  darkMode,
}: CheckoutReminderProps) {
  const { t } = useTranslation();
  const [timeRemaining, setTimeRemaining] = useState<{ hours: number; minutes: number } | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showReminder, setShowReminder] = useState(false);

  useEffect(() => {
    const calculateTimeRemaining = () => {
      const now = new Date();
      // Check-out date is a local calendar day — new Date('YYYY-MM-DD') is UTC
      // and put the countdown on the wrong day for guests west of UTC.
      const checkOut = parseLocalDate(checkOutDate);
      if (!checkOut) {
        setShowReminder(false);
        setTimeRemaining(null);
        return;
      }

      // Parse check-out time ("HH:MM"). Without a host-set time the countdown
      // runs to the end of the morning (only for the maths — never displayed).
      const [hours, minutes] = (checkOutTime || '10:00').split(':').map(Number);
      checkOut.setHours(hours || 0, minutes || 0, 0, 0);

      const diff = checkOut.getTime() - now.getTime();

      // Show reminder only if checkout is within 24 hours
      if (diff > 0 && diff <= 24 * 60 * 60 * 1000) {
        setShowReminder(true);
        const hoursLeft = Math.floor(diff / (1000 * 60 * 60));
        const minutesLeft = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
        setTimeRemaining({ hours: hoursLeft, minutes: minutesLeft });
      } else if (diff <= 0 && diff > -12 * 60 * 60 * 1000) {
        // Checkout time has just passed — thank the guest for the rest of
        // that day, not for the whole week the link stays open afterwards.
        setShowReminder(true);
        setTimeRemaining({ hours: 0, minutes: 0 });
      } else {
        setShowReminder(false);
        setTimeRemaining(null);
      }
    };

    calculateTimeRemaining();
    const interval = setInterval(calculateTimeRemaining, 60000); // Update every minute

    return () => clearInterval(interval);
  }, [checkOutDate, checkOutTime]);

  if (!showReminder) return null;

  const isCheckoutTime = timeRemaining && timeRemaining.hours === 0 && timeRemaining.minutes === 0;
  const isUrgent = timeRemaining && timeRemaining.hours < 2;

  return (
    <div
      className={`relative rounded-3xl overflow-hidden transition-all duration-300 ${
        darkMode
          ? isUrgent
            ? 'bg-slate-900 border-2 border-orange-500/60 shadow-2xl shadow-orange-500/20'
            : 'bg-slate-900 border-2 border-cyan-500/60 shadow-2xl shadow-cyan-500/20'
          : isUrgent
            ? 'bg-white border-2 border-orange-400/80 shadow-2xl shadow-orange-200/40'
            : 'bg-white border-2 border-cyan-400/80 shadow-2xl shadow-cyan-200/40'
      }`}
    >
      {/* Subtle gradient overlay */}
      <div
        className="absolute inset-0 opacity-5 pointer-events-none"
        style={{
          background: isUrgent
            ? 'radial-gradient(circle at top right, rgba(251, 146, 60, 0.3), transparent)'
            : 'radial-gradient(circle at top right, rgba(6, 182, 212, 0.3), transparent)',
        }}
      />

      {/* Header */}
      <div className="relative p-6">
        <div className="flex items-start gap-5">
          {/* Icon with STRONG animations */}
          <div
            className={`relative p-4 rounded-2xl ${
              isCheckoutTime
                ? 'bg-gradient-to-br from-green-500/40 to-emerald-500/40 shadow-2xl shadow-green-500/50'
                : isUrgent
                  ? 'bg-gradient-to-br from-orange-500/40 to-red-500/40 shadow-2xl shadow-orange-500/60'
                  : darkMode
                    ? 'bg-gradient-to-br from-cyan-500/40 to-teal-500/40 shadow-2xl shadow-cyan-500/50'
                    : 'bg-gradient-to-br from-cyan-500/40 to-teal-500/40 shadow-2xl shadow-cyan-400/50'
            }`}
            style={{
              animation: 'icon-bounce 2s ease-in-out infinite',
            }}
          >
            {/* STRONG Pulsing glow ring */}
            <div
              className={`absolute inset-0 rounded-2xl ${
                isCheckoutTime
                  ? 'bg-green-400/40'
                  : isUrgent
                    ? 'bg-orange-400/50'
                    : 'bg-cyan-400/40'
              }`}
              style={{
                animation: 'glow-pulse 2s ease-in-out infinite',
                filter: 'blur(12px)',
              }}
            />
            {isCheckoutTime ? (
              <CheckCircle2
                className={`relative w-8 h-8 ${
                  darkMode ? 'text-green-400' : 'text-green-600'
                }`}
              />
            ) : isUrgent ? (
              <AlertCircle
                className={`relative w-8 h-8 ${
                  darkMode ? 'text-orange-400' : 'text-orange-600'
                }`}
              />
            ) : (
              <Clock
                className={`relative w-8 h-8 ${
                  darkMode ? 'text-cyan-400' : 'text-cyan-600'
                }`}
              />
            )}
          </div>

          {/* Content */}
          <div className="relative flex-1">
            <h3
              className={`font-bold text-xl mb-3 ${
                darkMode ? 'text-white' : 'text-gray-900'
              }`}
              style={{
                textShadow: isUrgent
                  ? '0 0 20px rgba(251, 146, 60, 0.3)'
                  : '0 0 20px rgba(6, 182, 212, 0.2)',
              }}
            >
              {isCheckoutTime
                ? t('guest.checkout.thankYou')
                : isUrgent
                  ? t('guest.checkout.checkoutSoon')
                  : t('guest.checkout.checkoutToday')}
            </h3>

            {/* Time remaining - NO ANIMATIONS */}
            {timeRemaining && !isCheckoutTime && (
              <div className="mt-4 flex items-center gap-3">
                <div
                  className={`px-5 py-3 rounded-2xl ${
                    isUrgent
                      ? darkMode
                        ? 'bg-orange-500/25 border-2 border-orange-400/50 shadow-lg'
                        : 'bg-orange-100 border-2 border-orange-300 shadow-lg'
                      : darkMode
                        ? 'bg-cyan-500/20 border-2 border-cyan-400/40 shadow-lg'
                        : 'bg-cyan-100 border-2 border-cyan-300 shadow-lg'
                  }`}
                >
                  <div className="flex flex-col items-center">
                    <span
                      className={`text-4xl font-black tabular-nums ${
                        isUrgent
                          ? darkMode
                            ? 'text-orange-300'
                            : 'text-orange-700'
                          : darkMode
                            ? 'text-cyan-300'
                            : 'text-cyan-700'
                      }`}
                    >
                      {timeRemaining.hours}
                    </span>
                    <span
                      className={`text-xs font-semibold uppercase tracking-wider mt-1 ${
                        darkMode ? 'text-slate-300' : 'text-gray-600'
                      }`}
                    >
                      {t('guest.checkout.hours')}
                    </span>
                  </div>
                </div>
                <div
                  className={`px-5 py-3 rounded-2xl ${
                    isUrgent
                      ? darkMode
                        ? 'bg-orange-500/25 border-2 border-orange-400/50 shadow-lg'
                        : 'bg-orange-100 border-2 border-orange-300 shadow-lg'
                      : darkMode
                        ? 'bg-cyan-500/20 border-2 border-cyan-400/40 shadow-lg'
                        : 'bg-cyan-100 border-2 border-cyan-300 shadow-lg'
                  }`}
                >
                  <div className="flex flex-col items-center">
                    <span
                      className={`text-4xl font-black tabular-nums ${
                        isUrgent
                          ? darkMode
                            ? 'text-orange-300'
                            : 'text-orange-700'
                          : darkMode
                            ? 'text-cyan-300'
                            : 'text-cyan-700'
                      }`}
                    >
                      {timeRemaining.minutes}
                    </span>
                    <span
                      className={`text-xs font-semibold uppercase tracking-wider mt-1 ${
                        darkMode ? 'text-slate-300' : 'text-gray-600'
                      }`}
                    >
                      {t('guest.checkout.minutes')}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Checkout time info — only a time the host actually set */}
            {checkOutTime && (
            <div
              className={`mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full backdrop-blur-sm ${
                darkMode
                  ? 'bg-slate-700/30 border border-slate-600/50'
                  : 'bg-white/50 border border-gray-200'
              }`}
            >
              <Clock className={`w-4 h-4 ${darkMode ? 'text-cyan-400' : 'text-cyan-600'}`} />
              <p
                className={`text-sm font-medium ${
                  darkMode ? 'text-slate-200' : 'text-gray-700'
                }`}
              >
                {t('guest.checkout.checkoutAt')}: <span className="font-bold">{formatGuestTime(checkOutTime)}</span>
              </p>
            </div>
            )}
          </div>
        </div>
      </div>

      {/* Instructions (expandable) with modern styling */}
      {checkOutInstructions && (
        <>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className={`relative w-full px-6 py-4 flex items-center justify-between border-t-2 transition-all duration-300 ${
              darkMode
                ? 'border-slate-600/30 hover:bg-slate-700/20'
                : 'border-gray-200/50 hover:bg-white/50'
            } ${isExpanded ? 'bg-opacity-50' : ''}`}
          >
            <span
              className={`text-sm font-bold uppercase tracking-wide ${
                darkMode ? 'text-slate-200' : 'text-gray-800'
              }`}
            >
              {t('guest.checkout.instructions')}
            </span>
            <div
              className={`p-2 rounded-lg transition-all duration-300 ${
                darkMode
                  ? 'bg-cyan-500/20 text-cyan-400'
                  : 'bg-cyan-100 text-cyan-600'
              }`}
              style={{
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
              }}
            >
              <ChevronDown className="w-5 h-5" />
            </div>
          </button>

          {isExpanded && (
            <div
              className={`relative px-6 pb-6 pt-3 ${
                darkMode ? 'text-slate-300' : 'text-gray-700'
              }`}
              style={{
                animation: 'slide-down 0.3s ease-out',
              }}
            >
              <div
                className={`text-sm leading-relaxed whitespace-pre-wrap p-4 rounded-xl backdrop-blur-sm ${
                  darkMode
                    ? 'bg-slate-700/20 border border-slate-600/30'
                    : 'bg-white/50 border border-gray-200/50'
                }`}
              >
                {checkOutInstructions}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default CheckoutReminder;
